import { GEISINGER_AUTH_CONNECTION } from 'utils/auth-connection';
import { LeagueConfig, Environment } from '@leagueplatform/core';
import { createReactRouterAdapter } from '@leagueplatform/react-router-adapter';
import DocumentsEmptyState from 'assets/documents-empty-state.svg';
import FeatureHighlightBackground from 'assets/feature-highlight-background.svg';
import MessagingEmptyMessages from 'assets/messaging-empty-messages.svg';
import { APP_ID, TENANT_ID } from 'common/constants';
import { LoadingSpinner } from 'components/loading-spinner/loading-spinner.component';
import { ToastMessage } from 'components/toast-message/toast-message.component';
import EN_LOCALES from 'locales/en.json';
import ES_LOCALES from 'locales/es.json';
import { healthCoTheme } from 'theme/theme';
import { isAgentSession } from 'utils/agent-session';
import { signOut } from 'utils/sign-out';

const {
  VITE_SEGMENT_KEY,
  VITE_WS_API_URL,
  VITE_API_URL,
  VITE_LEGACY_REST_API_URL,
  VITE_CONTENT_URL,
  VITE_AUTH0_DOMAIN,
  VITE_AUTH0_CLIENT_ID,
  VITE_APP_ENV,
} = import.meta.env;

const authConfig = {
  domain: VITE_AUTH0_DOMAIN!,
  client_id: VITE_AUTH0_CLIENT_ID!,
  redirect_uri: window.location.origin,
};

// Support agents are warned five minutes before the idle timeout rather than the
// SDK default of two and a half; they have no option to extend the session, so
// the warning is their cue to wrap up. The config is only read at boot, so this
// keys off the tab flag set before the agent's login redirect. Members keep the
// SDK default.
const AGENT_IDLE_WARNING_MILLIS = 5 * 60 * 1000;

const analyticsConfig = {
  segmentKey: VITE_SEGMENT_KEY,
};

const apiConfig = {
  wsUrl: VITE_WS_API_URL!,
  url: VITE_API_URL!,
  legacyRestApi: VITE_LEGACY_REST_API_URL!,
};

// The generated Geisinger theme (theme.ts, v1.3.0) ships the `pill` and `circle`
// radii as 0, which renders fully-round components (e.g. the Switch toggle) as
// squares. Override them to fully-round here. theme.ts is auto-generated from
// Figma ("Do not modify directly"), so the fix lives at the app seam until the
// theme is regenerated with the correct radius variables. See GEIS-358.
const geisingerTheme: typeof healthCoTheme = {
  ...healthCoTheme,
  borderRadii: {
    ...healthCoTheme.borderRadii,
    circle: 99999,
    pill: 99999,
  },
};

export const config: LeagueConfig = {
  core: {
    clientId: APP_ID,
    tenantId: TENANT_ID,
    auth: {
      clientOptions: authConfig,
      idleSessionLifetimeHours: 0.25, // Default 15 minutes, should match the auth0 session inactivity timeout
      // Match the iOS/Android SDKs, which always send `prompt=login`. Without it
      // Auth0 reuses its SSO session cookie and signs the member straight back
      // in with no credentials. See GEIS-1839.
      // Auth0-deploy templates/geisinger/env-template/tenant.yaml defines the
      // customer OAuth2 connection as `geisinger`. Skip Universal Login's
      // connection chooser and start the customer IDP flow (GEIS-331).
      redirectLoginOptions: {
        prompt: 'login',
        connection: GEISINGER_AUTH_CONNECTION,
      },
      // The SDK default logs out without `federated`, which would leave an idle
      // timeout ending a weaker session than an explicit sign-out. See GEIS-1841.
      sessionTimeoutOptions: {
        onSessionExpire: signOut,
        ...(isAgentSession() && {
          warningBufferMillis: AGENT_IDLE_WARNING_MILLIS,
        }),
      },
    },
    analytics: analyticsConfig,
    api: apiConfig,
    appEnvironment: VITE_APP_ENV as Environment,
    contentUrl: VITE_CONTENT_URL!,
    routing: { adapter: createReactRouterAdapter() },
    ui: {
      theme: geisingerTheme,
      components: {
        toastMessage: { component: ToastMessage },
        pageHeader: {
          shape: 'line',
        },
        loading: {
          element: <LoadingSpinner />,
          text: false,
        },
      },
    },
    i18n: {
      strings: {
        default: EN_LOCALES,
        en: EN_LOCALES,
        es: ES_LOCALES,
      },
    },
  },
  assets: {
    overrides: {
      FEATURE_HIGHLIGHT_BACKGROUND: FeatureHighlightBackground,
      EMPTY_STATE_ICON: DocumentsEmptyState,
      MESSAGING_EMPTY_MESSAGES: MessagingEmptyMessages,
    },
  },
  benefits: {
    // Geisinger forks the core benefits landing page so the recent-claims
    // widget uses a 90-day window (PRD B-7) instead of core's 180. Without
    // this the SDK default requests core$benefits$landingPage$root and the
    // tenant nodes are never served. See GEIS-1914.
    benefitsLandingMasonryConfigKey: 'benefits$landingPage$root',
    // Geisinger also forks the core benefits detail root so three tenant-only
    // sub-pages (health-support-services, health-wellness-offerings,
    // healthy-rewards) can hang off the generic /benefits/:pageType route.
    // Masonry sends core$-prefixed ids to the shared registry and everything
    // else to the tenant registry, with no fallback, so the fork stays
    // unreachable until this override is in place. The id is deliberately
    // unprefixed: the spec reader strips the tenant segment from the node's
    // folder path, so nodes/geisinger/benefits/detailPage/root.yaml is keyed
    // benefits$detailPage$root, matching the landing key above.
    benefitsDetailsMasonryConfigKey: 'benefits$detailPage$root',
  },
  claims: {
    enableDynamicFilters: true,
    showHelpfulLinks: false,
    showAdditionalResources: false,
    featureFlags: {
      shouldShowClaimDetails: true,
    },
  },
  careDiscovery: {
    // The Masonry page supplies the Get Care heading and header spacing.
    showGetCareLandingHeadingText: false,
    masonryAppVersion: '2.0',
    masonryAppId: {
      left: 'careLanding$rootWeb',
    },
  },
  careDocuments: {
    shouldUseDocumentsV2: false,
  },
  wallet: {
    showLandingPageClaimsEntry: true,
  },
  messaging: {
    newMessagesPollIntervalSeconds: 30,
  },
  __internal: {
    shouldApplyCSSReset: true,
    shouldApplyLegacyGlobalStyles: true,
    shouldIgnoreDOMLangAttributeForLocale: true,
  },
};
